import { AcceleratorDto, EraDto } from "src/app/services/generatedApi";
import { calculateEraScore } from "../../score";

export const generateEraCsvReport = (eraDto: EraDto): string => {
  try {
    const scores = calculateEraScore(eraDto);

    const escapeCSV = (value: any): string => {
      if (value === null || value === undefined) return "";
      const str = String(value);

      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const boolToYesNo = (value: boolean | undefined): string => {
      return value ? "Yes" : "No";
    };

    const safeAccess = (
      obj: any,
      path: string[],
      defaultVal: any = "",
    ): any => {
      return path.reduce(
        (o, key) => (o && o[key] !== undefined ? o[key] : defaultVal),
        obj,
      );
    };

    const formatDate = (dateStr: string | undefined): string => {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        return date.toISOString().split("T")[0];
      } catch (e) {
        return "";
      }
    };

    let csvContent = "";

    const addRow = (cells: any[]): void => {
      if (cells.length === 0) {
        csvContent += ",\n";
      } else {
        csvContent += cells.map((cell) => escapeCSV(cell)).join(",") + "\n";
      }
    };

    addRow(["ERA Assessment Report"]);
    addRow([]);

    addRow(["Job Information"]);
    addRow(["Job Name", eraDto.jobInfo.jobName || ""]);
    addRow(["Department", eraDto.jobInfo.department || ""]);
    addRow(["Assessor", eraDto.jobInfo.assessor || ""]);
    addRow(["Date", formatDate(eraDto.jobInfo.date as string)]);
    addRow(["Shift", eraDto.jobInfo.shift || ""]);
    addRow([
      "Cycle Time",
      eraDto.jobInfo.cycleTime ? `${eraDto.jobInfo.cycleTime} sec` : "",
    ]);
    addRow([]);

    addRow(["Risk Factor Assessment"]);
    addRow([
      "Section",
      "Factor",
      "Back",
      "Neck",
      "Left Shoulder",
      "Right Shoulder",
      "Left Elbow",
      "Right Elbow",
      "Left Wrist",
      "Right Wrist",
    ]);

    addRow([
      "Posture",
      "Flexed",
      boolToYesNo(eraDto.back.posture.flexedOver20),
      boolToYesNo(eraDto.neck.posture.flexedOver30),
      boolToYesNo(eraDto.leftShoulder.posture.flexedOver45),
      boolToYesNo(eraDto.rightShoulder.posture.flexedOver45),
      boolToYesNo(
        eraDto.leftElbow.posture.flexedUnder60 ||
          eraDto.leftElbow.posture.flexedOver100,
      ),
      boolToYesNo(
        eraDto.rightElbow.posture.flexedUnder60 ||
          eraDto.rightElbow.posture.flexedOver100,
      ),
      boolToYesNo(eraDto.leftWrist.posture.flexedOver45),
      boolToYesNo(eraDto.rightWrist.posture.flexedOver45),
    ]);

    addRow([
      "Posture",
      "Extended",
      boolToYesNo(eraDto.back.posture.extended),
      boolToYesNo(eraDto.neck.posture.extended),
      boolToYesNo(eraDto.leftShoulder.posture.extended),
      boolToYesNo(eraDto.rightShoulder.posture.extended),
      "Not Applicable", // Elbows don't have extended posture
      "Not Applicable", // Elbows don't have extended posture
      boolToYesNo(eraDto.leftWrist.posture.extendedOver45),
      boolToYesNo(eraDto.rightWrist.posture.extendedOver45),
    ]);

    addRow([
      "Posture",
      "Sideways/Abducted",
      boolToYesNo(eraDto.back.posture.sidewaysOver20),
      boolToYesNo(eraDto.neck.posture.sideways),
      boolToYesNo(eraDto.leftShoulder.posture.abductedOver45),
      boolToYesNo(eraDto.rightShoulder.posture.abductedOver45),
      "Not Applicable", // Elbows don't have sideways/abducted posture
      "Not Applicable", // Elbows don't have sideways/abducted posture
      boolToYesNo(
        eraDto.leftWrist.posture.radialDeviatedOver15 ||
          eraDto.leftWrist.posture.ulnarDeviatedOver20,
      ),
      boolToYesNo(
        eraDto.rightWrist.posture.radialDeviatedOver15 ||
          eraDto.rightWrist.posture.ulnarDeviatedOver20,
      ),
    ]);

    addRow([
      "Posture",
      "Twisted/Rotated",
      boolToYesNo(eraDto.back.posture.twistedOver20),
      boolToYesNo(eraDto.neck.posture.twisted),
      "Not Applicable", // Shoulders don't have twisted posture
      "Not Applicable", // Shoulders don't have twisted posture
      boolToYesNo(eraDto.leftElbow.posture.forearmRotated),
      boolToYesNo(eraDto.rightElbow.posture.forearmRotated),
      "Not Applicable", // Wrists don't have twisted/rotated posture
      "Not Applicable", // Wrists don't have twisted/rotated posture
    ]);

    addRow([
      "Severe Posture",
      "Severe Flexion",
      boolToYesNo(eraDto.back.severePosture.flexedOver45),
      boolToYesNo(eraDto.neck.severePosture.flexedOver60),
      boolToYesNo(eraDto.leftShoulder.severePosture.flexedOver90),
      boolToYesNo(eraDto.rightShoulder.severePosture.flexedOver90),
      "Not Applicable", // Elbows don't have severe flexion
      "Not Applicable", // Elbows don't have severe flexion
      boolToYesNo(eraDto.leftWrist.severePosture.flexedOver65),
      boolToYesNo(eraDto.rightWrist.severePosture.flexedOver65),
    ]);

    addRow([
      "Severe Posture",
      "Severe Extension",
      boolToYesNo(eraDto.back.severePosture.extendedOver20),
      boolToYesNo(eraDto.neck.severePosture.extendedOver30),
      "Not Applicable", // Shoulders don't have severe extension
      "Not Applicable", // Shoulders don't have severe extension
      "Not Applicable", // Elbows don't have severe extension
      "Not Applicable", // Elbows don't have severe extension
      boolToYesNo(eraDto.leftWrist.severePosture.extendedOver65),
      boolToYesNo(eraDto.rightWrist.severePosture.extendedOver65),
    ]);

    addRow([
      "Severe Posture",
      "Severe Abduction",
      "Not Applicable", // Back doesn't have severe abduction
      "Not Applicable", // Neck doesn't have severe abduction
      boolToYesNo(eraDto.leftShoulder.severePosture.abductedOver90),
      boolToYesNo(eraDto.rightShoulder.severePosture.abductedOver90),
      "Not Applicable", // Elbows don't have severe abduction
      "Not Applicable", // Elbows don't have severe abduction
      "Not Applicable", // Wrists don't have severe abduction
      "Not Applicable", // Wrists don't have severe abduction
    ]);

    addRow([
      "Severe Posture",
      "Severe Rotation",
      "Not Applicable", // Back doesn't have severe rotation (separate from twist)
      "Not Applicable", // Neck doesn't have severe rotation (separate from twist)
      "Not Applicable", // Shoulders don't have severe rotation
      "Not Applicable", // Shoulders don't have severe rotation
      boolToYesNo(eraDto.leftElbow.severePosture.forearmRotatedOver60),
      boolToYesNo(eraDto.rightElbow.severePosture.forearmRotatedOver60),
      "Not Applicable", // Wrists don't have severe rotation
      "Not Applicable", // Wrists don't have severe rotation
    ]);

    addRow([
      "Force",
      "Weight/Force",
      boolToYesNo(eraDto.back.force.over10lb),
      "Not Applicable", // Neck doesn't have force assessment
      boolToYesNo(
        eraDto.leftShoulder.force.over10lb1Hand ||
          eraDto.leftShoulder.force.over15lb2Hand,
      ),
      boolToYesNo(
        eraDto.rightShoulder.force.over10lb1Hand ||
          eraDto.rightShoulder.force.over15lb2Hand,
      ),
      boolToYesNo(
        eraDto.leftElbow.force.over10lb1Hand ||
          eraDto.leftElbow.force.over15lb2Hand,
      ),
      boolToYesNo(
        eraDto.rightElbow.force.over10lb1Hand ||
          eraDto.rightElbow.force.over15lb2Hand,
      ),
      boolToYesNo(
        eraDto.leftWrist.force.pinchGripOver2lb ||
          eraDto.leftWrist.force.powerGripOver10lb,
      ),
      boolToYesNo(
        eraDto.rightWrist.force.pinchGripOver2lb ||
          eraDto.rightWrist.force.powerGripOver10lb,
      ),
    ]);

    addRow([
      "Repetition",
      "Frequency",
      boolToYesNo(safeAccess(eraDto, ["back", "repetition", "over2PerMin"])),
      boolToYesNo(safeAccess(eraDto, ["neck", "repetition", "over3PerMin"])),
      boolToYesNo(
        safeAccess(eraDto, ["leftShoulder", "repetition", "over2Point5PerMin"]),
      ),
      boolToYesNo(
        safeAccess(eraDto, [
          "rightShoulder",
          "repetition",
          "over2Point5PerMin",
        ]),
      ),
      boolToYesNo(
        safeAccess(eraDto, ["leftElbow", "repetition", "over10PerMin"]),
      ),
      boolToYesNo(
        safeAccess(eraDto, ["rightElbow", "repetition", "over10PerMin"]),
      ),
      boolToYesNo(
        safeAccess(eraDto, ["leftWrist", "repetition", "over15PerMin"]),
      ),
      boolToYesNo(
        safeAccess(eraDto, ["rightWrist", "repetition", "over15PerMin"]),
      ),
    ]);

    addRow([
      "Static Exertion",
      ">10 Seconds",
      boolToYesNo(safeAccess(eraDto, ["back", "staticExertion", "over10Sec"])),
      boolToYesNo(safeAccess(eraDto, ["neck", "staticExertion", "over10Sec"])),
      boolToYesNo(
        safeAccess(eraDto, ["leftShoulder", "staticExertion", "over10Sec"]),
      ),
      boolToYesNo(
        safeAccess(eraDto, ["rightShoulder", "staticExertion", "over10Sec"]),
      ),
      boolToYesNo(
        safeAccess(eraDto, ["leftElbow", "staticExertion", "over10Sec"]),
      ),
      boolToYesNo(
        safeAccess(eraDto, ["rightElbow", "staticExertion", "over10Sec"]),
      ),
      boolToYesNo(
        safeAccess(eraDto, ["leftWrist", "staticExertion", "over10Sec"]),
      ),
      boolToYesNo(
        safeAccess(eraDto, ["rightWrist", "staticExertion", "over10Sec"]),
      ),
    ]);

    addRow([]);

    addRow(["Accelerators"]);
    addRow(["Factor", "Present", "Body Part Affected", "Task"]);

    const formatAccelerator = (key: keyof AcceleratorDto): string[] => {
      const accelerator = eraDto.accelerators[key];
      return [
        key.toString(),
        boolToYesNo(!!accelerator),
        accelerator?.bodyPartAffected || "",
        accelerator?.task || "",
      ];
    };

    addRow(formatAccelerator("vibration"));
    addRow(formatAccelerator("contactStress"));
    addRow(formatAccelerator("impactStress"));

    addRow([]);

    addRow(["Assessment Scores"]);
    addRow(["Body Part", "Score", "Risk Level"]);

    const getRiskLevel = (score: number): string => {
      if (score <= 2) return "Low";
      if (score <= 5) return "Moderate";
      if (score <= 8) return "High";
      return "Very High";
    };

    addRow(["Back", scores.backScore, getRiskLevel(scores.backScore)]);
    addRow(["Neck", scores.neckScore, getRiskLevel(scores.neckScore)]);
    addRow([
      "Left Shoulder",
      scores.leftShoulderScore,
      getRiskLevel(scores.leftShoulderScore),
    ]);
    addRow([
      "Right Shoulder",
      scores.rightShoulderScore,
      getRiskLevel(scores.rightShoulderScore),
    ]);
    addRow([
      "Left Elbow",
      scores.leftElbowScore,
      getRiskLevel(scores.leftElbowScore),
    ]);
    addRow([
      "Right Elbow",
      scores.rightElbowScore,
      getRiskLevel(scores.rightElbowScore),
    ]);
    addRow([
      "Left Wrist",
      scores.leftWristScore,
      getRiskLevel(scores.leftWristScore),
    ]);
    addRow([
      "Right Wrist",
      scores.rightWristScore,
      getRiskLevel(scores.rightWristScore),
    ]);
    addRow([
      "Accelerators",
      scores.acceleratorScore,
      getRiskLevel(scores.acceleratorScore),
    ]);
    addRow(["Overall Total", scores.totalScore, scores.riskLevel]);

    return csvContent;
  } catch (error) {
    console.error("Error generating ERA CSV report:", error);
    throw error;
  }
};
